import { createIntl } from "@formatjs/intl";

import { TVehicle } from "types";

import de from "./envkv.json";

export enum EnergyTypes {
  VERBRENNER = "verbrenner",
  ELEKTRO = "elektro",
  HYBRID = "hybrid",
  WASSERSTOFF = "wasserstoff",
  ERDGAS = "erdgas",
}
export const getEnergyType = (vehicle: TVehicle): string => {
  if (vehicle.kraftstoff.name === "Elektro") return EnergyTypes.ELEKTRO;
  if (vehicle.kraftstoff.name === "Wasserstoff") return EnergyTypes.WASSERSTOFF;
  if (vehicle.kraftstoff.name === "Erdgas H/CNG") return EnergyTypes.ERDGAS;
  if (vehicle.plugin_hybrid === true) return EnergyTypes.HYBRID;
  return EnergyTypes.VERBRENNER;
};

export const isNeuwagen = (vehicle: TVehicle): boolean => {
  if (!vehicle.erstzulassung) return true;

  const now = new Date();
  const barrier = new Date(
    now.getFullYear(),
    now.getMonth() - 8,
    now.getDate()
  );
  const ez = new Date(vehicle.erstzulassung);
  const isNewByEZ = ez > barrier;

  const isNewByKM = !vehicle.km || vehicle.km <= 1000;

  return isNewByEZ || isNewByKM;
};

const flattenObject = (obj: any): any => {
  let resultObj: any = {};

  for (const i in obj) {
    if (typeof obj[i] === "object" && !Array.isArray(obj[i])) {
      const tempObj = flattenObject(obj[i]);
      for (const j in tempObj) {
        resultObj[i + "." + j] = tempObj[j];
      }
    } else {
      resultObj[i] = obj[i];
    }
  }
  return resultObj;
};

const intl = createIntl({
  locale: "de",
  defaultLocale: "de",
  messages: flattenObject(de),
});

export const t = (id: string) => intl.formatMessage({ id });
export const n = (
  value: any,
  unit: string | null = null,
  digits: number = 1
): string | undefined => {
  return Number.isNaN(parseInt(value))
    ? undefined
    : intl.formatNumber(value, {
        minimumFractionDigits: digits,
        maximumFractionDigits: digits,
      }) + (!!unit ? ` ${unit}` : "");
};

export const getEnVKV = (vehicle: TVehicle): any => {
  const {
    kraftstoff: { id: kraftstoffId },
    modell,
    fzart,
  } = vehicle;
  if (fzart.id === 4) return null;

  const fields = ["title"];

  const EnVKV = new Map();

  fields.map((field) => EnVKV.set(field, t(field)));

  const information = new Map();
  information
    .set("marke", modell.marke.name)
    .set("handelsbezeichnung", modell.name)
    .set("antriebsart", t(`kraftstoff.${kraftstoffId}.antriebsart`))
    .set("kraftstoff", t(`kraftstoff.${kraftstoffId}.kraftstoff`))
    .set(
      "anderer_energietraeger",
      t(`kraftstoff.${kraftstoffId}.anderer_energietraeger`)
    )
    .set(
      "umweltplakette",
      vehicle.umweltplakette ? vehicle.umweltplakette.name : undefined
    )
    .set("schadstoff", vehicle.schadstoff ? vehicle.schadstoff.name : undefined)
    .set("plugin_hybrid", vehicle.plugin_hybrid)
    .set("hubraum", vehicle.hubraum);

  EnVKV.set("information", information);

  let summary = new Map();

  if (vehicle.wltp_co2klasse !== null)
    summary.set("wltp_co2klasse", {
      label: t("label.wltp_co2klasse"),
      value: vehicle.wltp_co2klasse || undefined,
    });

  if (vehicle.wltp_co2klasse_entladen !== null)
    summary.set("wltp_co2klasse_entladen", {
      label: t("label.wltp_co2klasse_entladen"),
      value: vehicle.wltp_co2klasse_entladen || undefined,
    });

  if (vehicle.wltp_kombiniert)
    summary.set("wltp_kombiniert", {
      label: t("label.wltp_kombiniert"),
      value: n(
        vehicle.wltp_kombiniert,
        t(`kraftstoff.${kraftstoffId}.wltp_kombiniert.unit`)
      ),
    });

  if (vehicle.wltp_stromverbrauch_kombiniert !== null)
    summary.set("wltp_stromverbrauch_kombiniert", {
      label: t("label.wltp_stromverbrauch_kombiniert"),
      value: n(
        vehicle.wltp_stromverbrauch_kombiniert,
        t(`kraftstoff.${kraftstoffId}.wltp_stromverbrauch_kombiniert.unit`)
      ),
    });

  if (vehicle.wltp_kombiniert_entladen !== null)
    summary.set("wltp_kombiniert_entladen", {
      label: t("label.wltp_kombiniert_entladen"),
      value: n(
        vehicle.wltp_kombiniert_entladen,
        t(`kraftstoff.${kraftstoffId}.wltp_kombiniert_entladen.unit`)
      ),
    });

  if ((vehicle.wltp_co2_emissionen !== null) !== null)
    summary.set("wltp_co2_emissionen", {
      label: t("label.wltp_co2_emissionen"),
      value: n(
        vehicle.wltp_co2_emissionen,
        t(`kraftstoff.${kraftstoffId}.wltp_co2_emissionen.unit`)
      ),
    });
  if (vehicle.wltp_co2_emissionen_entladen !== null)
    summary.set("wltp_co2_emissionen_entladen", {
      label: t("label.wltp_co2_emissionen_entladen"),
      value: n(
        vehicle.wltp_co2_emissionen_entladen,
        t(`kraftstoff.${kraftstoffId}.wltp_co2_emissionen_entladen.unit`)
      ),
    });

  if (vehicle.wltp_kombiniert_entladen !== null)
    summary.set("wltp_kombiniert_entladen", {
      label: t("label.wltp_kombiniert_entladen"),
      value: n(
        vehicle.wltp_kombiniert_entladen,
        t(`kraftstoff.${kraftstoffId}.wltp_kombiniert_entladen.unit`),
        0
      ),
    });

  if (vehicle.wltp_stromverbrauch_kombiniert !== null)
    summary.set("wltp_stromverbrauch_kombiniert", {
      label: t("label.wltp_stromverbrauch_kombiniert"),
      value: vehicle.wltp_stromverbrauch_kombiniert || undefined,
    });

  if (vehicle.wltp_stromverbrauch_kombiniert_reiner_batteriebetrieb !== null)
    summary.set("wltp_stromverbrauch_kombiniert_reiner_batteriebetrieb", {
      label: t("label.wltp_stromverbrauch_kombiniert_reiner_batteriebetrieb"),
      value:
        vehicle.wltp_stromverbrauch_kombiniert_reiner_batteriebetrieb ||
        undefined,
    });

  EnVKV.set("summary", summary);

  return EnVKV;
};

export const stringify = (_map: any, divider: string = " ; "): any => {
  const result: string[] = [];
  _map.forEach(
    (value: { label: string; value: string; unit: string }, key: string) => {
      if (!value.value) return;
      if (Array.isArray(value.value)) {
        switch (value.value.length) {
          case 2:
            if (value.value[0] === value.value[1]) {
              if (value.value[0] !== undefined) {
                result.push(
                  `${value.label}: ${value.value[0]}${value.unit || ""}`
                );
              }
            } else {
              result.push(
                `${value.label}: ${value.value.join("-")}${value.unit || ""}`
              );
            }
            break;
        }
      } else {
        result.push(`${value.label}: ${value.value}${value.unit || ""}`);
      }
    }
  );
  return result.join(divider);
};
